import React, { useState, useMemo, useEffect } from "react";
import { queryCache, useQuery } from "react-query";
import { useAsync } from "@applied-med/ui/dist/hooks";
import { get, post, getForNotifications } from "_shared/request";
import { user, JwtToken, msalJwtToken } from "_shared/auth";
import GDRAPI from "./GenericDocumentRouting/GDRAPI";
import ITProjectsAPI from "./ITProjects/ITProjectsAPI";
import PORAPI from "./POR/PORAPI";
import ServiceNowAPI from "./ServiceNow/ServiceNowAPI";
// import * as FORMSAPI from "./Forms/FormAPI/Form.queries";
import useViewport from "_shared/useViewport";
import { graphql } from "_shared/request";
import { useFeature } from "_shared/feature";
import { getEnv } from "../../utils/constants";
import { findIndex, propEq, isEmpty, pathOr } from "ramda";

export const fetchApiKey = async (key) => {
  let apiKey = "";
  const queryString = `
          query MyQuery {
            api_secrets {
              api_key
              id
              used_for
            }
          }
        `;
  try {
    const secretKey = getEnv(key) || key;
    const response = await graphql(queryString);
    for (const item of response.data.data.api_secrets) {
      if (item.used_for === secretKey) {
        apiKey = item.api_key;
        break;
      }
    }
    return apiKey;
  } catch (e) {
    console.log("An error occurred trying to fetch the api key.", error);
  }
};

const getPendingActions = async () => {
  try {
    const url = `${getEnv("DECISIONS2_ENDPOINT")}/`;
    const { data } = await get({
      url,
      params: {
        FlowAlias: "PendingActionsAPI_GetPendingActions",
        sessionid: JwtToken,
        ForceRefresh: "False",
        TeamMember: user.TeamId,
        outputtype: "RawJson",
        action: "api",
      },
    });

    return data;
  } catch (e) {
    console.log(e);
    return {};
  }
};

export const useGetLMSELearningPendings = () => {
  const queryFn = async () => {
    try {
      const { data } = await post({
        url: `${getEnv("LMS_NONQS_URL")}/Team/PendingActions`,
        data: {
          source: "TeamMembers",
          sourceId: user.TeamId,
        },
      });
      return data;
    } catch (error) {
      return {
        LearningPending: 0,
        CertificationsPending: 0,
        LearningDue: 0,
        CertificationsManagementPendingActions: 0,
        CertificationsManagementPendingActionsNonAdmin: 0,
      };
    }
  };

  return useQuery(["LMSELearningPending", user.TeamId], queryFn, {
    enabled: !!user?.TeamId,
    staleTime: 1000 * 60,
    refetchOnMount: true,
  });
};

export const useGetLMSSOPPendings = () => {
  const queryFn = async () => {
    try {
      const { data } = await get({
        url: `${getEnv("LMS_ROOT_URL")}/TeamMember/${
          user?.TeamId
        }?filter=Pending`,
      });
      return data;
    } catch (error) {
      return { Assignments: [] };
    }
  };

  return useQuery(["LMSSOPPending", user.TeamId], queryFn, {
    enabled: !!user?.TeamId,
    staleTime: 1000 * 60,
    refetchOnMount: true,
  });
};

export const useGeteSORPendings = () => {
  const queryFn = async () => {
    try {
      const { data } = await get({
        url: `${getEnv("BASEURL")}/esor/totalpendingactions/${user?.TeamId}`,
      });
      return data;
    } catch (error) {
      return { Done: { TotalPending: 0 } };
    }
  };

  return useQuery(["eSORPending", user.TeamId], queryFn, {
    enabled: !!user?.TeamId,
    staleTime: 1000 * 60,
    refetchOnMount: true,
  });
};

export const useGetOPARPendings = () => {
  const queryFn = async () => {
    try {
      const OPAR_API_KEY = await fetchApiKey("OPAR_API_KEY");
      const { data } = await get({
        url: `${getEnv(
          "OPAR_BASE_URL"
        )}/opar/assignments/pending?UserSource=TeamMembers&UserSourceId=${
          user?.TeamId
        }`,
        headers: {
          "X-Api-Key": OPAR_API_KEY,
        },
      });
      return data;
    } catch (error) {
      return { count: 0 };
    }
  };

  return useQuery(["OPARPending", user.TeamId], queryFn, {
    enabled: !!user?.TeamId,
    staleTime: 1000 * 60,
    refetchOnMount: true,
  });
};

export const useGetShopOrderPendings = () => {
  const queryFn = async () => {
    try {
      const SHOPORDER_API_KEY = await fetchApiKey("SHOPORDER_API_KEY");
      const { data } = await get({
        url: `${getEnv(
          "SHOPORDER_BASE_URL"
        )}?TeamMemberSource=TeamMembers&TeamMemberId=${user?.TeamId}`,
        headers: {
          "X-Api-Key": SHOPORDER_API_KEY,
        },
      });
      return data;
    } catch (error) {
      return { pendingRequestsCount: 0 };
    }
  };

  return useQuery(["ShopOrderPending", user.TeamId], queryFn, {
    enabled: !!user?.TeamId,
    staleTime: 1000 * 60,
    refetchOnMount: true,
  });
};

export const useGetBSDPendings = () => {
  const queryFn = async () => {
    try {
      const BSD_API_KEY = await fetchApiKey("BSD_API_KEY");
      const { data } = await get({
        url: `${getEnv("BSD_BASE_URL")}/Inbox/get-count-for-user?UserId=${
          user?.TeamId
        }&UserSource=TeamMembers`,
        headers: {
          "X-Api-Key": BSD_API_KEY,
        },
      });
      return data;
    } catch (error) {
      return { pendingRequestsCount: 0 };
    }
  };

  return useQuery(["BSDPending", user.TeamId], queryFn, {
    enabled: !!user?.TeamId,
    staleTime: 1000 * 60,
    refetchOnMount: true,
  });
};

const usePendingActions = (config = {}) => {
  const queryFn = async () => {
    const result = await getPendingActions();
    if (!result) {
      return { Items: [], TotalPending: 0 }; // return default values if result is undefined
    }
    const { Items, TotalPending } = result;
    const cleanedItems = Array.isArray(Items)
      ? Items.filter((el) => el.Name !== "Purchase Order Requests").map(
          (el) => ({
            ...el,
            Name: el.Name,
          })
        )
      : [];
    return { Items: cleanedItems, TotalPending };
  };

  return useQuery("pending-actions", queryFn, {
    staleTime: 1000 * 5,
    ...config,
  });
};

const DEFAULT_ITEMS = {
  ["Purchase Order Requests"]: {
    Name: "Purchase Order Requests",
    Url: "/notifications/por/approvals",
    Total: 0,
    hasAccess: true,
  },
  ["ServiceNow"]: {
    Name: "ServiceNow",
    Url: "/notifications/servicenow/approvals",
    Total: 0,
    hasAccess: true,
  },
  ["General Document Routing"]: {
    Name: "General Document Routing",
    Url: "/notifications/generic-document-routing",
    Total: 0,
    hasAccess: true,
  },
  ["ARC Request Approvals"]: {
    Name: "ARC Request Approvals",
    Url: "/notifications/request-portal",
    Total: 0,
    hasAccess: true,
  },
  ["Product Complaint Form"]: {
    Name: "Product Complaint Form",
    Url: "/notifications/forms/PCF",
    Total: 0,
    hasAccess: true,
  },
  ["Learning Management System"]: {
    Name: "Learning Management System",
    Url: "https://learning.appliedmedical.com/",
    isSAP: false,
    Total: 0,
    HasTotal: true,
  },
  ["eSOR"]: {
    Name: "eSOR - Electronic Shop Order Request",
    Url: "https://decisionsprd2sa.appliedmed.com/Primary/H/?FolderId=750eff99-88fd-11ec-916f-005056bf3f1b&pageName=eSOR%20Home%20Page%20v2&Chrome=Off",
    isSAP: false,
    Total: 0,
    HasTotal: true,
  },
  ["OPAR"]: {
    Name: "OPAR - Outstanding Planned Actions Report",
    isSAP: false,
    Total: 0,
    HasTotal: true,
  },
  ["Shop Order"]: {
    Name: "Shop Order",
    isSAP: false,
    Total: 0,
    HasTotal: true,
  },
  ["BSD"]: {
    Name: "BSD - Biological Sciences Department",
    isSAP: false,
    Total: 0,
    HasTotal: true,
  },
  // ["IQ DMS - Workflow Inbox"]: {
  //   Name: "IQ DMS - Workflow Inbox",
  //   Url: "https://gw.appliedmed.com/zlpd/shells/abap/FioriLaunchpad.html#WorkflowTask-displayInbox?allItems=true",
  //   Total: 0,
  //   hasAccess: true,
  // },
};

const defaultNotificationConfig = {
  retry: false,
  staleTime: 1000 * 60,
  refetchOnMount: true,
  refetchInterval: 1000 * 60,
  // refetchIntervalInBackground: true,
};

export const useNotifications = (config = {}) => {
  const viewport = useViewport();

  const eLearningData = useGetLMSELearningPendings();
  const sopData = useGetLMSSOPPendings();
  const eSORData = useGeteSORPendings();
  const OPARData = useGetOPARPendings();
  const shopOrderData = useGetShopOrderPendings();
  const BSDData = useGetBSDPendings();

  const [items, setItems] = useState(() => {
    const { Items: pendingActions = [] } =
      queryCache.getQueryData("pending-actions") || {};
    const PORApprovals = queryCache.getQueryData("por-approvals")?.items || [];
    const ServiceNowApprovals =
      queryCache.getQueryData("servicenow-approvals")?.items || [];
    const GDRApprovals = queryCache.getQueryData("gdr-approvals") || [];
    const ITApprovals =
      queryCache.getQueryData("it-projects-request-approvals")?.assignments ||
      [];
    console.log("ITApprovals", ITApprovals);
    // const PCFApprovals = queryCache.getQueryData("pcf-approvals") || [];
    // console.log({ pendingActions });
    const eSOR = queryCache.getQueryData(["eSORPending", user?.TeamId]);

    const OPAR = queryCache.getQueryData(["OPARPending", user?.TeamId]);
    const ShopOrder = queryCache.getQueryData([
      "ShopOrderPending",
      user?.TeamId,
    ]);
    const BSD = queryCache.getQueryData(["BSDPending", user?.TeamId]);

    const eLearning = queryCache.getQueryData([
      "LMSELearningPending",
      user?.TeamId,
    ]);
    const SOP = queryCache.getQueryData(["LMSSOPPending", user?.TeamId]);

    const LMSTotalPending =
      (eLearning?.LearningPending || 0) +
      (eLearning?.CertificationsPending || 0) +
      (eLearning?.LearningDue || 0) +
      (eLearning?.CertificationsManagementPendingActions || 0) +
      (eLearning?.CertificationsManagementPendingActionsNonAdmin || 0) +
      (SOP?.Assignments?.length || 0);

    return pendingActions.reduce((acc, el) => ({ ...acc, [el.Name]: el }), {
      ["Purchase Order Requests"]: {
        ...DEFAULT_ITEMS["Purchase Order Requests"],
        Total: PORApprovals.length,
        // hasAccess:
        //   !queryCache.getQuery("por-approvals") ||
        //   queryCache.getQuery("por-approvals")?.state.failureCount < 1,
      },
      ["ServiceNow"]: {
        ...DEFAULT_ITEMS["ServiceNow"],
        Total: ServiceNowApprovals.length,
        hasAccess: true,
        //   !queryCache.getQuery("se
        // rvicenow-approvals") ||
        //   queryCache.getQuery("servicenow-approvals")?.state.failureCount < 1,
      },
      ["General Document Routing"]: {
        ...DEFAULT_ITEMS["General Document Routing"],
        Total: GDRApprovals.length,
        hasAccess:
          !queryCache.getQuery("gdr-approvals") ||
          queryCache.getQuery("gdr-approvals")?.state.failureCount < 1,
      },
      ["ARC Request Approvals"]: {
        ...DEFAULT_ITEMS["ARC Request Approvals"],
        Total: ITApprovals.length,
        hasAccess:
          !queryCache.getQuery("it-projects-request-approvals") ||
          queryCache.getQuery("it-projects-request-approvals")?.state
            .failureCount < 1,
      },
      ["Product Complaint Form"]: {
        ...DEFAULT_ITEMS["Product Complaint Form"],
        Total: 0,
        // hasAccess: !queryCache.getQuery("pcf-approvals")?.state.failure < 100,
        hasAccess: true,
      },
      ["Learning Management System"]: {
        ...DEFAULT_ITEMS["Learning Management System"],
        Total: LMSTotalPending,
      },
      ["eSOR"]: {
        ...DEFAULT_ITEMS["eSOR"],
        Total: eSOR?.Done?.TotalPending || 0,
      },
      ["OPAR"]: {
        ...DEFAULT_ITEMS["OPAR"],
        Total: OPAR?.count || 0,
        Url: OPAR?.link || "",
      },
      ["Shop Order"]: {
        ...DEFAULT_ITEMS["Shop Order"],
        Total: ShopOrder?.pendingRequestsCount || 0,
        Url: ShopOrder?.siteLink || "",
      },
      ["BSD"]: {
        ...DEFAULT_ITEMS["BSD"],
        Total: BSD?.pendingRequestsCount || 0,
        Url: BSD?.siteLink || "",
      },
    });
  });

  const isPORApprovalsQueryEnabled =
    !!msalJwtToken &&
    items["Purchase Order Requests"].hasAccess &&
    (!queryCache.getQuery("por-approvals") ||
      queryCache.getQuery("por-approvals")?.state?.failureCount < 2);

  const PORApprovalsQuery = PORAPI.useApprovals({
    ...config,
    ...defaultNotificationConfig,
    enabled: isPORApprovalsQueryEnabled,
    refetchOnMount: isPORApprovalsQueryEnabled,
    refetchOnWindowFocus: isPORApprovalsQueryEnabled,
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 30,
    retry: 3,

    onSuccess: (data) => {
      setItems((prevState) => {
        const POR = prevState["Purchase Order Requests"];
        // console.log({POR});
        return {
          ...prevState,
          [POR.Name]: {
            ...POR,
            Total: data.items.length,
            Url: "/notifications/por/approvals",
          },
        };
      });
    },
    onError: () => {
      setItems((prevState) => {
        const item = prevState["Purchase Order Requests"];

        return {
          ...prevState,
          [item.Name]: { ...item, Total: 0, hasAccess: false },
        };
      });
    },
  });

  const isServiceNowApprovalsQueryEnabled =
    !!msalJwtToken &&
    items["ServiceNow"].hasAccess &&
    (!queryCache.getQuery("servicenow-approvals") ||
      queryCache.getQuery("servicenow-approvals")?.state?.failureCount < 2);

  const ServiceNowApprovalsQuery = ServiceNowAPI.useApprovals({
    ...config,
    ...defaultNotificationConfig,
    enabled: isServiceNowApprovalsQueryEnabled,
    refetchOnMount: isServiceNowApprovalsQueryEnabled,
    refetchOnWindowFocus: isServiceNowApprovalsQueryEnabled,
    staleTime: 1000 * 30,
    refetchInterval: 1000 * 30,
    retry: 3,

    onSuccess: (data) => {
      setItems((prevState) => {
        const ServiceNow = prevState["ServiceNow"];
        return {
          ...prevState,
          [ServiceNow.Name]: {
            ...ServiceNow,
            Total: data.items?.length || 0,
            Url: "/notifications/servicenow/approvals",
          },
        };
      });
    },
    onError: () => {
      setItems((prevState) => {
        const item = prevState["ServiceNow"];

        return {
          ...prevState,
          [item.Name]: { ...item, Total: 0, hasAccess: false },
        };
      });
    },
  });

  const isGDRApprovalsQueryEnabled =
    !!JwtToken &&
    user.TeamId &&
    items["General Document Routing"].hasAccess &&
    (!queryCache.getQuery("it-projects-approvals") ||
      queryCache.getQuery("it-projects-approvals")?.state.failureCount < 1);

  const GDRApprovalsQuery = GDRAPI.useApprovals({
    ...config,
    ...defaultNotificationConfig,
    enabled: isGDRApprovalsQueryEnabled,
    refetchOnMount: isGDRApprovalsQueryEnabled,
    refetchOnWindowFocus: isGDRApprovalsQueryEnabled,
    onSuccess: (data) => {
      setItems((prevState) => {
        const item = prevState["General Document Routing"];

        return { ...prevState, [item.Name]: { ...item, Total: data.length } };
      });
    },
    onError: () => {
      setItems((prevState) => {
        const item = prevState["General Document Routing"];

        return {
          ...prevState,
          [item.Name]: { ...item, Total: 0, hasAccess: false },
        };
      });
    },
  });

  const isITRequestApprovalsQueryEnabled =
    !!JwtToken &&
    user.TeamId &&
    items["ARC Request Approvals"].hasAccess &&
    (!queryCache.getQuery("it-projects-request-approvals") ||
      queryCache.getQuery("it-projects-request-approvals")?.state.failureCount <
        1);

  const ITRequestApprovalsQuery = ITProjectsAPI.useRequestApprovals({
    ...config,
    ...defaultNotificationConfig,
    enabled: isITRequestApprovalsQueryEnabled,
    refetchOnMount: isITRequestApprovalsQueryEnabled,
    refetchOnWindowFocus: isITRequestApprovalsQueryEnabled,
    onSuccess: (data) => {
      setItems((prevState) => {
        const item = prevState["ARC Request Approvals"];

        return {
          ...prevState,
          [item.Name]: {
            ...item,
            Total: data?.assignments ? data.assignments.length : 0,
          },
        };
      });
    },
    onError: () => {
      setItems((prevState) => {
        const item = prevState["ARC Request Approvals"];

        return {
          ...prevState,
          [item.Name]: { ...item, Total: 0, hasAccess: false },
        };
      });
    },
  });

  const pendingActionsQuery = usePendingActions({
    ...config,
    ...defaultNotificationConfig,
    enabled: !!JwtToken && user.TeamId,
    onSuccess: ({ Items }) => {
      setItems((prevState) => {
        let newState = Items.reduce(
          (acc, el) => ({ ...acc, [el.Name]: el }),
          prevState
        );
        // newState["Purchase Order Requests"]["Url"] =
        //   DEFAULT_ITEMS["Purchase Order Requests"].Url;
        // newState["Purchase Order Requests"]["IsSAP"] = false;
        return newState;
      });
    },
  });

  const notificationsState = useAsync({
    queries: {
      PORApprovalsQuery,
      ServiceNowApprovalsQuery,
      // GDRApprovalsQuery,
      // ITRequestApprovalsQuery,
      pendingActionsQuery,
    },
  });

  // useEffect(() => {
  //   console.log({ notificationsState });
  // }, [notificationsState]);

  useEffect(() => {
    setItems((prevItems) => ({
      ...prevItems,
      ["OPAR"]: {
        ...prevItems["OPAR"],
        Total: OPARData?.data?.count,
        Url: OPARData?.data?.link,
      },
    }));
  }, [OPARData.data]);

  useEffect(() => {
    setItems((prevItems) => ({
      ...prevItems,
      ["Shop Order"]: {
        ...prevItems["Shop Order"],
        Total: shopOrderData?.data?.pendingRequestsCount,
        Url: shopOrderData?.data?.siteLink,
      },
    }));
  }, [shopOrderData.data]);

  useEffect(() => {
    setItems((prevItems) => ({
      ...prevItems,
      ["BSD"]: {
        ...prevItems["BSD"],
        Total: BSDData?.data?.pendingRequestsCount,
        Url: BSDData?.data?.siteLink,
      },
    }));
  }, [BSDData.data]);

  useEffect(() => {
    setItems((prevItems) => ({
      ...prevItems,
      ["eSOR"]: {
        ...prevItems["eSOR"],
        Total: eSORData?.data?.Done?.TotalPending,
      },
    }));
  }, [eSORData.data]);

  useEffect(() => {
    let instructorCertificates =
      eLearningData?.data?.CertificationsManagementPendingActions || 0;
    let nonAdmin =
      eLearningData?.data?.CertificationsManagementPendingActionsNonAdmin || 0;
    const total =
      (eLearningData?.data?.LearningPending || 0) +
      (eLearningData?.data?.CertificationsPending || 0) +
      (eLearningData?.data?.LearningDue || 0) +
      nonAdmin +
      instructorCertificates +
      (sopData?.data?.Assignments?.length || 0);

    setItems((prevItems) => ({
      ...prevItems,
      ["Learning Management System"]: {
        ...prevItems["Learning Management System"],
        Total: total,
      },
    }));
  }, [eLearningData.data, sopData.data]);

  useEffect(() => {
    if (items["Purchase Order Requests"]) {
      const POR = items["Purchase Order Requests"];

      setItems((prevState) => ({
        ...prevState,
        [POR.Name]: {
          ...POR,
          // Url: !viewport.isMobile
          //   ? //  PORApprovalsQuery?.data?.desktopLink ?? "zpor:zpor?env=ZGW_PRD"
          //     "zpor:zpor?env=ZGW_PRD"
          //   : "/notifications/por/approvals",
          Url: "/notifications/por/approvals",
        },
      }));
    }
  }, [viewport.isMobile, PORApprovalsQuery.data]);

  useEffect(() => {
    if (items["ServiceNow"]) {
      const ServiceNow = items["ServiceNow"];

      setItems((prevState) => ({
        ...prevState,
        [ServiceNow.Name]: {
          ...ServiceNow,
          Url: "/notifications/servicenow/approvals",
        },
      }));
    }
  }, [viewport.isMobile, ServiceNowApprovalsQuery.data]);

  // const iqdms = {
  //   ...DEFAULT_ITEMS["IQ DMS - Workflow Inbox"],
  //   Total: 0,
  //   // hasAccess: !queryCache.getQuery("pcf-approvals")?.state.failure < 100,
  //   hasAccess: true,
  // };

  const sortedItems = useMemo(
    () => Object.values(items).sort((a, b) => b.Total - a.Total),
    [items]
  );

  // if (
  //   findIndex(propEq("Name", "IQ DMS - Workflow Inbox"))(sortedItems) === -1
  // ) {
  //   const index = findIndex(
  //     propEq("Name", "PLM - Product Lifecycle Management ")
  //   )(sortedItems);
  //   sortedItems.splice(index + 1, 0, iqdms);
  // }

  const totalPending = useMemo(
    () =>
      Object.values(items).reduce(
        (acc, { Total }) => (Total ? acc + Total : acc),
        0
      ),
    [items]
  );
  // console.log({
  //   sortedItems,
  // });

  return { data: { items: sortedItems, totalPending }, ...notificationsState };
};
